/**
 * QueryCachePersister - User-scoped React Query persistence with hydration gate
 *
 * Phase 5F: Deterministic hydration - blocks children until cache is ready
 *
 * This component handles:
 * - Hydrating cache from IndexedDB when user is known (BEFORE children mount)
 * - Persisting cache changes to IndexedDB (debounced with maxWait)
 * - Clearing cache on user change/logout
 * - Must be placed INSIDE AuthProvider (needs useAuth)
 *
 * Key behaviors:
 * - BLOCKS rendering of children until hydration complete (deterministic)
 * - For logged-out users, renders children immediately (no IndexedDB read)
 * - Persists on unmount and periodically
 * - Clears cache when userId changes (user switch or logout)
 *
 * Persistence starvation fix (Phase 5G):
 * - Debounce with maxWait: guarantees persist within 2s even if changes keep happening
 * - Serialized persists: single-flight + one-deep queue prevents thrash
 * - On page exit: immediate flush (no waiting for debounce)
 */

import { useEffect, useRef, useCallback, useState, createContext, useContext, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { hydrate, dehydrate } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  createIndexedDbPersister,
  clearUserCache,
  dehydrateOptions,
} from '../lib/queryPersistence';

// Debounce time for persisting cache changes (ms)
const PERSIST_DEBOUNCE_MS = 2000;

// Max wait time - guarantee at least one persist within this time even under continuous changes
const PERSIST_MAX_WAIT_MS = 2000;

// Periodic persist interval (ms) - save every 30s while active
const PERSIST_INTERVAL_MS = 30 * 1000;

// ============================================================================
// Hydration Context - exposes hydration state to children
// ============================================================================

interface HydrationContextValue {
  isHydrated: boolean;
  hydratedUserId: string | null;
}

const HydrationContext = createContext<HydrationContextValue>({
  isHydrated: false,
  hydratedUserId: null,
});

/**
 * Hook to check if cache hydration is complete.
 * Useful for components that need to know if persisted data is available.
 */
export function useCacheHydration() {
  return useContext(HydrationContext);
}

// ============================================================================
// Persist Flush Context - exposes immediate persist trigger to children
// ============================================================================

interface PersistFlushContextValue {
  /**
   * Immediately flush the current cache to IndexedDB.
   * Use after critical data (e.g., For You) successfully loads to ensure
   * persistence before potential page refresh. Serialized: safe to call multiple times.
   */
  flushNow: () => void;
}

const PersistFlushContext = createContext<PersistFlushContextValue>({
  flushNow: () => {},
});

/**
 * Hook to trigger immediate cache persistence.
 * Use after critical queries succeed to ensure data is persisted before page unload.
 */
export function usePersistFlush() {
  return useContext(PersistFlushContext);
}

// ============================================================================
// QueryCachePersister - Main component
// ============================================================================

interface QueryCachePersisterProps {
  children: ReactNode;
}

export function QueryCachePersister({ children }: QueryCachePersisterProps) {
  const queryClient = useQueryClient();
  const { user, initialized } = useAuth();
  const userId = user?.id ?? null;

  // Hydration state - blocks children until hydration is complete
  const [isHydrated, setIsHydrated] = useState(false);
  const [hydratedUserId, setHydratedUserId] = useState<string | null>(null);

  // Track previous userId to detect changes
  const prevUserIdRef = useRef<string | null>(null);

  // Debounce timer for persist
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Periodic persist interval
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Serialization refs for single-flight + one-deep queue pattern
  const lastPersistTimeRef = useRef<number>(0);
  const persistInFlightRef = useRef<Promise<void> | null>(null);
  const persistQueuedRef = useRef<boolean>(false);

  // Raw persist function (NOT serialized - used internally)
  const persistCacheRaw = useCallback(
    async (targetUserId: string) => {
      if (!targetUserId) return;

      const persister = createIndexedDbPersister(targetUserId);
      const dehydratedState = dehydrate(queryClient, dehydrateOptions);

      await persister.persistClient({
        timestamp: Date.now(),
        buster: '',
        clientState: dehydratedState,
      });

      lastPersistTimeRef.current = Date.now();
    },
    [queryClient]
  );

  // Serialized persist: single-flight + one-deep queue
  // Prevents concurrent IndexedDB writes and ensures latest state is always persisted
  const persistCache = useCallback(
    async (targetUserId: string) => {
      if (!targetUserId) return;

      // If a persist is already in flight, queue one more (latest state) and return
      if (persistInFlightRef.current) {
        persistQueuedRef.current = true;
        return;
      }

      // Start the persist
      persistInFlightRef.current = (async () => {
        try {
          await persistCacheRaw(targetUserId);
        } catch (err) {
          console.error('[QueryCachePersister] Persist failed:', err);
        } finally {
          persistInFlightRef.current = null;

          // If queued, run exactly ONE more persist (then clear queued)
          if (persistQueuedRef.current) {
            persistQueuedRef.current = false;
            // Schedule microtask to allow state to settle
            queueMicrotask(() => persistCache(targetUserId));
          }
        }
      })();

      await persistInFlightRef.current;
    },
    [persistCacheRaw]
  );

  // Hydrate cache from IndexedDB for given userId (with timeout to prevent hangs)
  const hydrateCache = useCallback(
    async (targetUserId: string) => {
      if (!targetUserId) return;

      const persister = createIndexedDbPersister(targetUserId);
      const HYDRATION_TIMEOUT_MS = 3000;

      let hydrationTimeoutId: ReturnType<typeof setTimeout> | null = null;
      try {
        const restorePromise = persister.restoreClient();
        const timeoutPromise = new Promise<null>((resolve) => {
          hydrationTimeoutId = setTimeout(() => {
            hydrationTimeoutId = null;
            console.warn('[QueryCachePersister] Hydration timeout - proceeding without persisted cache');
            resolve(null);
          }, HYDRATION_TIMEOUT_MS);
        });

        const persistedClient = await Promise.race([restorePromise, timeoutPromise]);

        if (persistedClient?.clientState) {
          hydrate(queryClient, persistedClient.clientState);
          console.log('[QueryCachePersister] Hydrated cache for user:', targetUserId);
        }
      } catch (err) {
        console.error('[QueryCachePersister] Failed to hydrate:', err);
      } finally {
        // Always clear timeout - covers both success and error paths
        if (hydrationTimeoutId) clearTimeout(hydrationTimeoutId);
      }

      setHydratedUserId(targetUserId);
      setIsHydrated(true);
    },
    [queryClient]
  );

  // Debounced persist with maxWait: guarantees at least one persist within PERSIST_MAX_WAIT_MS
  // even if changes keep happening (prevents starvation on busy boot)
  const throttledPersist = useCallback(
    (targetUserId: string) => {
      const now = Date.now();
      const timeSinceLastPersist = now - lastPersistTimeRef.current;

      // If we've exceeded maxWait since last persist, persist immediately
      if (timeSinceLastPersist >= PERSIST_MAX_WAIT_MS) {
        // Clear any pending debounce timer
        if (persistTimerRef.current) {
          clearTimeout(persistTimerRef.current);
          persistTimerRef.current = null;
        }
        // Persist immediately (serialized)
        persistCache(targetUserId);
        return;
      }

      // Otherwise, debounce as usual (but trailing persist will happen)
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
      }

      // Calculate remaining time until maxWait is reached
      const remainingMaxWait = PERSIST_MAX_WAIT_MS - timeSinceLastPersist;
      const delay = Math.min(PERSIST_DEBOUNCE_MS, remainingMaxWait);

      persistTimerRef.current = setTimeout(() => {
        persistCache(targetUserId);
        persistTimerRef.current = null;
      }, delay);
    },
    [persistCache]
  );

  // Handle auth initialization and user changes
  useEffect(() => {
    if (!initialized) return;

    const prevUserId = prevUserIdRef.current;

    // Case 1: No user (logged out or never logged in)
    if (!userId) {
      // If we had a previous user, persist their data before clearing
      if (prevUserId) {
        persistCache(prevUserId).then(() => {
          queryClient.clear();
          console.log('[QueryCachePersister] Logged out, cache cleared');
        });
      }
      // Mark as hydrated (no cache to load for anonymous users)
      setIsHydrated(true);
      setHydratedUserId(null);
      prevUserIdRef.current = null;
      return;
    }

    // Case 2: User logged in or switched users
    if (userId !== prevUserId) {
      const switchUser = async () => {
        // If switching users, persist old user's data first
        if (prevUserId) {
          await persistCache(prevUserId);
          queryClient.clear();
        }

        // Hydrate new user's cache (this sets isHydrated = true when done)
        await hydrateCache(userId);
      };

      // Only block hydration for actual user-switch (not fresh login from logged-out)
      // Fresh login hydrates in background without forcing a skeleton flash
      if (prevUserId) {
        setIsHydrated(false);
      }
      switchUser();
    }

    prevUserIdRef.current = userId;
  }, [userId, initialized, queryClient, persistCache, hydrateCache]);

  // Set up cache change listener for throttled persist (with maxWait)
  useEffect(() => {
    if (!userId || !isHydrated) return;

    // Subscribe to cache changes - uses throttledPersist for maxWait behavior
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      throttledPersist(userId);
    });

    // Set up periodic persist interval - uses serialized persistCache
    intervalRef.current = setInterval(() => {
      persistCache(userId);
    }, PERSIST_INTERVAL_MS);

    // Flush persistence immediately on page exit (pagehide + visibility hidden)
    // This ensures cache is saved before refresh/navigation, preventing data loss
    const flushOnExit = () => {
      // Clear pending debounce timer - we're persisting now
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
      // Use serialized persist (fire-and-forget) - do NOT bypass serialization
      // This prevents concurrent IndexedDB writes if a persist is already in-flight
      // Do NOT clear persistQueuedRef - serialization handles "latest state" correctly
      persistCache(userId);
    };

    const handlePageHide = () => {
      flushOnExit();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushOnExit();
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribe();

      // Clear timers
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Remove exit listeners
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      // Final persist on unmount (serialized)
      if (userId) {
        persistCache(userId);
      }
    };
  }, [userId, isHydrated, queryClient, throttledPersist, persistCache]);

  // Expose flushNow to children for immediate persistence after critical data loads
  const flushNow = useCallback(() => {
    if (userId && isHydrated) {
      // Clear pending debounce timer - we're persisting now
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
      // Persist immediately (serialized for safety)
      persistCache(userId);
    }
  }, [userId, isHydrated, persistCache]);

  // Context values
  const hydrationContextValue: HydrationContextValue = {
    isHydrated,
    hydratedUserId,
  };

  const persistFlushContextValue: PersistFlushContextValue = {
    flushNow,
  };

  // Block children until:
  // 1. Auth is initialized, AND
  // 2. Cache is hydrated (or no user to hydrate for)
  if (!initialized || !isHydrated) {
    return (
      <HydrationContext.Provider value={hydrationContextValue}>
        <PersistFlushContext.Provider value={persistFlushContextValue}>
          {/* Skeleton shell while hydrating - never render blank */}
          <div className="min-h-screen bg-[#111111] flex items-center justify-center">
            <div className="w-8 h-8 rounded-full bg-[#1A1F26] animate-pulse" />
          </div>
        </PersistFlushContext.Provider>
      </HydrationContext.Provider>
    );
  }

  return (
    <HydrationContext.Provider value={hydrationContextValue}>
      <PersistFlushContext.Provider value={persistFlushContextValue}>
        {children}
      </PersistFlushContext.Provider>
    </HydrationContext.Provider>
  );
}

/**
 * Hook to manually clear cache for current user
 * Useful for "clear cache" button in settings
 */
export function useClearQueryCache() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useCallback(async () => {
    if (user?.id) {
      await clearUserCache(user.id);
      queryClient.clear();
      console.log('[useClearQueryCache] Cache cleared');
    }
  }, [queryClient, user?.id]);
}
